/**
 * When Scout runs a job, as presets a person picks — not cron text.
 *
 * Scout keeps the schedule as a cron expression in a time zone; these helpers
 * turn a preset into that and back, say it in words, and list the next runs.
 */

export type Preset =
  | { kind: "every"; minutes: number }
  | { kind: "hourly" }
  | { kind: "daily"; time: string }
  | { kind: "manual" }
  | { kind: "custom"; cron: string };

export const EVERY_MINUTES = [5, 10, 15, 30];

export function cronOf(preset: Preset): string | null {
  switch (preset.kind) {
    case "every":
      return `*/${preset.minutes} * * * *`;
    case "hourly":
      return "0 * * * *";
    case "daily": {
      const [hour, minute] = splitTime(preset.time);
      return `${minute} ${hour} * * *`;
    }
    case "custom":
      return preset.cron;
    default:
      return null;
  }
}

function splitTime(time: string): [number, number] {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return [0, 0];
  return [Math.min(23, Number(match[1])), Math.min(59, Number(match[2]))];
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function presetOf(cron: string | null | undefined): Preset {
  if (!cron) return { kind: "manual" };
  const text = cron.trim().replace(/\s+/g, " ");
  const every = /^\*\/(\d+) \* \* \* \*$/.exec(text);
  if (every) return { kind: "every", minutes: Number(every[1]) };
  if (text === "0 * * * *") return { kind: "hourly" };
  const daily = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(text);
  if (daily) return { kind: "daily", time: `${pad(Number(daily[2]))}:${pad(Number(daily[1]))}` };
  return { kind: "custom", cron: text };
}

export function scheduleWords(preset: Preset, timeZone: string): string {
  switch (preset.kind) {
    case "every":
      return `Every ${preset.minutes} minutes`;
    case "hourly":
      return "Every hour, on the hour";
    case "daily":
      return `Every day at ${preset.time} (${timeZone} time)`;
    case "manual":
      return "Only when a person presses Run now";
    default:
      return cronWords(preset.cron) ?? `On Scout’s own timing: ${preset.cron}`;
  }
}

/** Common cron shapes that are not presets, in words. */
export function cronWords(cron: string): string | null {
  const text = cron.trim().replace(/\s+/g, " ");
  const hours = /^(\d{1,2}) \*\/(\d+) \* \* \*$/.exec(text);
  if (hours) {
    const minute = Number(hours[1]);
    return `Every ${hours[2]} hours, ${minute === 0 ? "on the hour" : `at ${minute} minutes past`}`;
  }
  const weekly = /^(\d{1,2}) (\d{1,2}) \* \* (\d)$/.exec(text);
  if (weekly) {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return `Every ${days[Number(weekly[3]) % 7]} at ${pad(Number(weekly[2]))}:${pad(Number(weekly[1]))}`;
  }
  return null;
}

/** How long omnium waits for a run before it asks Scout itself. */
export function checkSecondsFor(preset: Preset): number {
  switch (preset.kind) {
    case "every":
      return Math.max(60, preset.minutes * 60 + 300);
    case "hourly":
      return 3900;
    default:
      return 86400;
  }
}

type Parts = { minute: number; hour: number };

function partsIn(date: Date, timeZone: string): Parts {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    return { minute: read("minute"), hour: read("hour") };
  } catch {
    return { minute: date.getUTCMinutes(), hour: date.getUTCHours() };
  }
}

function matches(preset: Preset, parts: Parts): boolean {
  switch (preset.kind) {
    case "every":
      return preset.minutes > 0 && parts.minute % preset.minutes === 0;
    case "hourly":
      return parts.minute === 0;
    case "daily": {
      const [hour, minute] = splitTime(preset.time);
      return parts.hour === hour && parts.minute === minute;
    }
    default:
      return false;
  }
}

/** The next `count` runs after `from`, for the presets omnium understands. */
export function nextRuns(preset: Preset, timeZone: string, from: Date, count = 3): Date[] {
  if (preset.kind === "manual" || preset.kind === "custom") return [];
  const out: Date[] = [];
  const start = new Date(from.getTime());
  start.setUTCSeconds(0, 0);
  // Two days of minutes covers a daily run in any zone.
  for (let i = 1; i <= 2 * 24 * 60 + 60 && out.length < count; i += 1) {
    const moment = new Date(start.getTime() + i * 60_000);
    if (matches(preset, partsIn(moment, timeZone))) out.push(moment);
  }
  return out;
}

export function runTimeWords(date: Date, timeZone: string): string {
  try {
    return date.toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });
  } catch {
    return date.toISOString();
  }
}
